import type { LiveUpdateTokenResponse } from "@/logic/api";
import {
  createCrossTabCoordinator,
  type CrossTabCoordinator,
} from "./crossTabCoordinator";
import {
  appendLogEntry,
  createInitialDiagnostics,
  createSubscriptionDiagnostics,
  sanitiseLiveUpdateDiagnostics,
  type LiveUpdateCoordination,
  type LiveUpdateDiagnostics,
  type LiveUpdateFailureCategory,
  type LiveUpdateMode,
  type LiveUpdateState,
  type LiveUpdateSubscriptionDiagnostics,
} from "./diagnostics";
import type { LiveUpdateEvent } from "./liveUpdateMessage";
import {
  createRefreshCoordinator,
  type RefreshCoordinator,
} from "./refreshCoordinator";
import {
  createSocketIoTransport,
  type LiveUpdateTransport,
  type TransportStateChange,
} from "./socketIoTransport";

export type { LiveUpdateState };

export interface LiveUpdateSubscription {
  key: string;
  shouldRefresh(event: LiveUpdateEvent): boolean;
  refresh(): Promise<void>;
}

export interface LiveUpdates {
  subscribe(subscription: LiveUpdateSubscription): () => void;
  retry(): void;
}

interface LiveUpdatesClientOptions {
  enabled: boolean;
  diagnosticsEnabled?: boolean;
  identity: string | null;
  requestToken(signal: AbortSignal): Promise<LiveUpdateTokenResponse>;
  createTransport?: typeof createSocketIoTransport;
  createCoordinator?: typeof createCrossTabCoordinator;
  isVisible?: () => boolean;
  addVisibilityListener?: (listener: () => void) => () => void;
  now?: () => number;
}

interface SubscriptionRecord {
  subscription: LiveUpdateSubscription;
  refreshCoordinator: RefreshCoordinator;
  diagnostics: LiveUpdateSubscriptionDiagnostics;
  refreshStartedAt: number | null;
}

export interface LiveUpdatesClient extends LiveUpdates {
  start(): void;
  dispose(): void;
  getState(): LiveUpdateState;
  subscribeToState(listener: (state: LiveUpdateState) => void): () => void;
  getDiagnostics(): LiveUpdateDiagnostics;
  subscribeToDiagnostics(
    listener: (diagnostics: LiveUpdateDiagnostics) => void,
  ): () => void;
  forceDisconnect(): void;
  reconnect(): void;
  injectEvent(event: LiveUpdateEvent): void; // Only for diagnostics
  clearDiagnostics(): void;
  createSanitisedReport(): string;
}

const defaultIsVisible = (): boolean =>
  typeof document === "undefined" || document.visibilityState !== "hidden";

const defaultAddVisibilityListener = (listener: () => void): (() => void) => {
  if (typeof document === "undefined") return () => undefined;
  document.addEventListener("visibilitychange", listener);
  return () => document.removeEventListener("visibilitychange", listener);
};

/**
 * Deep, page-neutral module for live-update lifecycle, subscriptions, refreshes,
 * cross-tab ownership, and safe diagnostics.
 */
export const createLiveUpdatesClient = ({
  enabled,
  diagnosticsEnabled = false,
  identity,
  requestToken,
  createTransport = createSocketIoTransport,
  createCoordinator = createCrossTabCoordinator,
  isVisible = defaultIsVisible,
  addVisibilityListener = defaultAddVisibilityListener,
  now = Date.now,
}: LiveUpdatesClientOptions): LiveUpdatesClient => {
  const available = enabled && identity !== null;
  const subscriptions = new Map<symbol, SubscriptionRecord>();
  const failedSubscriptionRefreshes = new Set<symbol>();
  const stateListeners = new Set<(state: LiveUpdateState) => void>();
  const diagnosticsListeners = new Set<
    (diagnostics: LiveUpdateDiagnostics) => void
  >();
  let diagnostics = createInitialDiagnostics(enabled);
  let transportState: TransportStateChange | null = null;
  let coordinator: CrossTabCoordinator | null = null;
  let removeVisibilityListener: (() => void) | null = null;
  let started = false;
  let nextDiagnosticSubscriptionId = 1;

  const publishDiagnostics = (): void => {
    diagnosticsListeners.forEach((listener) => listener(diagnostics));
  };

  const setDiagnostics = (update: Partial<LiveUpdateDiagnostics>): void => {
    const keys = Object.keys(update) as Array<keyof LiveUpdateDiagnostics>;
    if (keys.every((key) => diagnostics[key] === update[key])) return;
    const previousState = diagnostics.state;
    const next = { ...diagnostics, ...update };
    const stateChanged = next.state !== previousState;
    diagnostics =
      diagnosticsEnabled && stateChanged
        ? {
          ...next,
          transitions: appendLogEntry(next.transitions, {
            at: now(),
            state: next.state,
            failureCategory: next.lastFailureCategory,
          }),
        }
        : next;
    publishDiagnostics();
    if (stateChanged) {
      stateListeners.forEach((listener) => listener(diagnostics.state));
    }
  };

  const subscriptionRows = (): readonly LiveUpdateSubscriptionDiagnostics[] =>
    [...subscriptions.values()].map((record) => record.diagnostics);

  const derivedState = (): LiveUpdateState => {
    if (!enabled) return "disabled";
    if (subscriptions.size === 0) return "idle";
    if (!available) return "unavailable";
    if (
      failedSubscriptionRefreshes.size > 0 &&
      transportState?.state === "connected"
    )
      return "delayed";
    return transportState?.state ?? "connecting";
  };

  const updateState = (
    failureCategory?: LiveUpdateFailureCategory | null,
    extra: Partial<LiveUpdateDiagnostics> = {},
  ): void => {
    setDiagnostics({
      ...extra,
      state: derivedState(),
      lastFailureCategory:
        failureCategory === undefined
          ? diagnostics.lastFailureCategory
          : failureCategory,
    });
  };

  const handleCoordinationChange = (
    coordination: LiveUpdateCoordination,
  ): void => {
    if (!diagnosticsEnabled) return;
    const previousMode = diagnostics.coordination.mode;
    const entered = (mode: LiveUpdateMode): number =>
      coordination.mode === mode && previousMode !== mode ? 1 : 0;
    setDiagnostics({
      coordination,
      becameOwnerCount: diagnostics.becameOwnerCount + entered("owner"),
      fellBackCount: diagnostics.fellBackCount + entered("per-tab"),
    });
  };

  const handleTransportState = (change: TransportStateChange): void => {
    transportState = change;
    updateState(
      change.failureCategory,
      diagnosticsEnabled
        ? {
          retryAttempt: change.retryAttempt ?? diagnostics.retryAttempt,
          tokenExpiresAt:
            change.tokenExpiresAt === undefined
              ? diagnostics.tokenExpiresAt
              : change.tokenExpiresAt,
        }
        : {},
    );
  };

  const handleMalformedMessage = (): void => {
    if (!diagnosticsEnabled) return;
    setDiagnostics({
      malformedEventCount: diagnostics.malformedEventCount + 1,
      lastFailureCategory: "invalid-message",
    });
  };

  const handleEvent = (event: LiveUpdateEvent): void => {
    const receivedAt = diagnosticsEnabled ? now() : 0;
    let accepted = false;

    subscriptions.forEach((record) => {
      let matches = false;
      try {
        matches = record.subscription.shouldRefresh(event);
      } catch {
        matches = false;
      }
      if (!matches) return;
      accepted = true;
      if (diagnosticsEnabled) {
        record.diagnostics = {
          ...record.diagnostics,
          acceptedEventCount: record.diagnostics.acceptedEventCount + 1,
        };
      }
      record.refreshCoordinator.request();
    });

    if (!diagnosticsEnabled) return;
    setDiagnostics({
      receivedEventCount: diagnostics.receivedEventCount + 1,
      acceptedEventCount: diagnostics.acceptedEventCount + (accepted ? 1 : 0),
      ignoredEventCount: diagnostics.ignoredEventCount + (accepted ? 0 : 1),
      lastEventAt: receivedAt,
      subscriptions: subscriptionRows(),
      events: appendLogEntry(diagnostics.events, {
        at: receivedAt,
        type: event.type,
        publishedAt: Date.parse(event.publishedAt),
        outcome: accepted ? "accepted" : "ignored",
        ...(event.summaryCount === undefined
          ? {}
          : { summaryCount: event.summaryCount }),
      }),
    });
  };

  const reconcile = (): void => {
    subscriptions.forEach(({ refreshCoordinator }) =>
      refreshCoordinator.reconcile(),
    );
  };

  const handleVisibilityChange = (): void => {
    const visible = isVisible();
    subscriptions.forEach(({ refreshCoordinator }) =>
      refreshCoordinator.setVisible(visible),
    );
  };

  const countActiveRefreshes = (): number =>
    [...subscriptions.values()].filter(
      (record) => record.refreshStartedAt !== null,
    ).length;

  const publishSubscriptionState = (
    failureCategory?: LiveUpdateFailureCategory | null,
    extra: Partial<LiveUpdateDiagnostics> = {},
  ): void => {
    updateState(
      failureCategory,
      diagnosticsEnabled
        ? {
          ...extra,
          activeRefreshCount: countActiveRefreshes(),
          subscriptions: subscriptionRows(),
        }
        : {},
    );
  };

  const syncSubscriptions = (): void => {
    publishSubscriptionState();
    coordinator?.setActiveCount(subscriptions.size);
  };

  const handleRefreshStarted = (record: SubscriptionRecord): void => {
    if (!diagnosticsEnabled) return;
    record.refreshStartedAt = now();
    record.diagnostics = {
      ...record.diagnostics,
      refreshStartedCount: record.diagnostics.refreshStartedCount + 1,
    };
    publishSubscriptionState(undefined, {
      refreshStartedCount: diagnostics.refreshStartedCount + 1,
    });
  };

  const handleRefreshCompleted = (
    id: symbol,
    record: SubscriptionRecord,
  ): void => {
    failedSubscriptionRefreshes.delete(id);
    const failureCategory =
      diagnostics.lastFailureCategory === "refresh" &&
        failedSubscriptionRefreshes.size === 0
        ? null
        : undefined;
    if (!diagnosticsEnabled) {
      updateState(failureCategory);
      return;
    }

    const completedAt = now();
    const startedAt = record.refreshStartedAt;
    record.refreshStartedAt = null;
    record.diagnostics = {
      ...record.diagnostics,
      refreshCompletedCount: record.diagnostics.refreshCompletedCount + 1,
      lastRefreshAt: completedAt,
      lastRefreshDurationMs: startedAt === null ? null : completedAt - startedAt,
    };
    publishSubscriptionState(failureCategory, {
      refreshCompletedCount: diagnostics.refreshCompletedCount + 1,
      lastRefreshAt: completedAt,
    });
  };

  const handleRefreshFailed = (id: symbol, record: SubscriptionRecord): void => {
    failedSubscriptionRefreshes.add(id);
    if (!diagnosticsEnabled) {
      updateState("refresh");
      return;
    }

    record.refreshStartedAt = null;
    record.diagnostics = {
      ...record.diagnostics,
      refreshFailedCount: record.diagnostics.refreshFailedCount + 1,
    };
    publishSubscriptionState("refresh", {
      refreshFailedCount: diagnostics.refreshFailedCount + 1,
    });
  };

  const ensureCoordinator = (): void => {
    if (coordinator || !started || !available || subscriptions.size === 0) {
      return;
    }

    const transport: LiveUpdateTransport = createTransport({
      requestToken,
      diagnosticsEnabled,
    });
    coordinator = createCoordinator({
      identity,
      transport,
      onEvent: handleEvent,
      onReconnect: reconcile,
      onStateChange: handleTransportState,
      ...(diagnosticsEnabled
        ? { onCoordinationChange: handleCoordinationChange }
        : {}),
      onMalformedMessage: handleMalformedMessage,
    });
    coordinator.start();
    coordinator.setActiveCount(subscriptions.size);
  };

  const client: LiveUpdatesClient = {
    start: () => {
      if (started) return;
      started = true;
      if (!available) {
        updateState(enabled ? "configuration" : null);
        return;
      }
      removeVisibilityListener = addVisibilityListener(handleVisibilityChange);
      ensureCoordinator();
    },
    subscribe: (subscription) => {
      if (!enabled) return () => undefined;
      const id = Symbol(subscription.key);
      const record: SubscriptionRecord = {
        subscription,
        diagnostics: createSubscriptionDiagnostics(
          nextDiagnosticSubscriptionId++,
        ),
        refreshStartedAt: null,
        refreshCoordinator: createRefreshCoordinator({
          refresh: subscription.refresh,
          initiallyVisible: isVisible(),
          onStarted: () => handleRefreshStarted(record),
          onCompleted: () => handleRefreshCompleted(id, record),
          onFailed: () => handleRefreshFailed(id, record),
        }),
      };
      subscriptions.set(id, record);
      if (transportState?.state === "connected") {
        // This page may have missed events while another page kept the socket open.
        record.refreshCoordinator.request();
      }
      syncSubscriptions();
      ensureCoordinator();

      let active = true;
      return () => {
        if (!active) return;
        active = false;
        record.refreshCoordinator.dispose();
        subscriptions.delete(id);
        record.refreshStartedAt = null;
        const removedFailedSubscription =
          failedSubscriptionRefreshes.delete(id);
        syncSubscriptions();
        if (
          removedFailedSubscription &&
          failedSubscriptionRefreshes.size === 0 &&
          diagnostics.lastFailureCategory === "refresh"
        ) {
          updateState(null);
        }
      };
    },
    retry: () => {
      failedSubscriptionRefreshes.clear();
      coordinator?.retry();
      reconcile();
      updateState(null);
    },
    dispose: () => {
      started = false;
      subscriptions.forEach(({ refreshCoordinator }) =>
        refreshCoordinator.dispose(),
      );
      subscriptions.clear();
      nextDiagnosticSubscriptionId = 1;
      failedSubscriptionRefreshes.clear();
      removeVisibilityListener?.();
      removeVisibilityListener = null;
      coordinator?.dispose();
      coordinator = null;
      transportState = null;
      setDiagnostics({
        state: enabled ? "idle" : "disabled",
        retryAttempt: 0,
        tokenExpiresAt: null,
        subscriptions: [],
        activeRefreshCount: 0,
        lastFailureCategory: null,
      });
    },
    getState: () => diagnostics.state,
    subscribeToState: (listener) => {
      stateListeners.add(listener);
      return () => stateListeners.delete(listener);
    },
    getDiagnostics: () => diagnostics,
    subscribeToDiagnostics: (listener) => {
      diagnosticsListeners.add(listener);
      return () => diagnosticsListeners.delete(listener);
    },
    forceDisconnect: () => coordinator?.forceDisconnect(),
    reconnect: () => coordinator?.reconnect(),
    injectEvent: (event) => {
      if (diagnosticsEnabled) handleEvent(event);
    },
    clearDiagnostics: () => {
      if (!diagnosticsEnabled) return;
      subscriptions.forEach((record) => {
        const reset = createSubscriptionDiagnostics(
          record.diagnostics.subscriptionId,
        );
        record.diagnostics =
          record.refreshStartedAt === null
            ? reset
            : { ...reset, refreshStartedCount: 1 };
      });
      const activeRefreshCount = countActiveRefreshes();
      diagnostics = {
        ...createInitialDiagnostics(enabled),
        state: derivedState(),
        coordination: diagnostics.coordination,
        retryAttempt: diagnostics.retryAttempt,
        tokenExpiresAt: diagnostics.tokenExpiresAt,
        lastFailureCategory: diagnostics.lastFailureCategory,
        refreshStartedCount: activeRefreshCount,
        activeRefreshCount,
        subscriptions: subscriptionRows(),
      };
      publishDiagnostics();
    },
    createSanitisedReport: () =>
      sanitiseLiveUpdateDiagnostics(diagnostics, {
        generatedAt: now(),
      }),
  };

  return client;
};
